import type { EntityState } from '@reduxjs/toolkit';
import { createSelector } from '@reduxjs/toolkit';

import { integrationsAdapter } from './integration.reducer';
import type {
  ClientConnection,
  IntegrationConfig,
  IntegrationEnvironment,
  IntegrationProvider,
} from './integration.schema';

// Get entity adapter selectors
const adapterSelectors = integrationsAdapter.getSelectors<
  EntityState<IntegrationConfig, string>
>(state => state);

// Select integration config by provider
export const selectIntegrationByProvider = (
  state: EntityState<IntegrationConfig, string>,
  provider: IntegrationProvider,
): IntegrationConfig | undefined => {
  return adapterSelectors.selectById(state, provider);
};

// Select all connections for a provider across all environments
export const selectConnectionsByProvider = createSelector(
  [
    (
      state: EntityState<IntegrationConfig, string>,
      provider: IntegrationProvider,
    ) => selectIntegrationByProvider(state, provider),
  ],
  (config): ClientConnection[] => {
    if (!config) {
      return [];
    }

    // Return all connections from the connections map
    return Object.values(config.connections);
  },
);

// Select connection by paragon connection ID
export const selectConnectionById = (
  state: EntityState<IntegrationConfig, string>,
  connectionId: string,
): ClientConnection | undefined => {
  const allConfigs = adapterSelectors.selectAll(state);

  for (const config of allConfigs) {
    const connection = config.connections[connectionId];
    if (connection) {
      return connection;
    }
  }
  return undefined;
};

// Select all integration configs
export const selectAllIntegrations = (
  state: EntityState<IntegrationConfig, string>,
): IntegrationConfig[] => {
  return adapterSelectors.selectAll(state);
};

// Select all integrations that have connections in a specific environment
export const selectIntegrationsInEnvironment = createSelector(
  [
    (state: EntityState<IntegrationConfig, string>) =>
      adapterSelectors.selectAll(state),
    (
      _state: EntityState<IntegrationConfig, string>,
      clientEnv: IntegrationEnvironment,
    ) => clientEnv,
  ],
  (allConfigs, clientEnv): IntegrationConfig[] => {
    return allConfigs.filter(
      config => config.clientEnvs[clientEnv] !== undefined,
    );
  },
);

// Check if an integration exists
export const selectIntegrationExists = (
  state: EntityState<IntegrationConfig, string>,
  provider: IntegrationProvider,
): boolean => {
  return adapterSelectors.selectById(state, provider) !== undefined;
};

// Select connection for a provider in a specific environment
export const selectConnectionInEnvironment = (
  state: EntityState<IntegrationConfig, string>,
  provider: IntegrationProvider,
  clientEnv: IntegrationEnvironment,
): ClientConnection | undefined => {
  const config = selectIntegrationByProvider(state, provider);
  if (!config) {
    return undefined;
  }

  // Get the connection ID from the environment and resolve it from the connections map
  const connectionId = config.clientEnvs[clientEnv];
  if (!connectionId) {
    return undefined;
  }

  return config.connections[connectionId];
};

// Select available functions for a provider
export const selectAvailableFunctions = createSelector(
  [
    (
      state: EntityState<IntegrationConfig, string>,
      provider: IntegrationProvider,
    ) => selectIntegrationByProvider(state, provider),
  ],
  (config): string[] => {
    return config?.availableFunctions || [];
  },
);

// Check if a specific function is enabled for a provider
export const selectIsFunctionEnabled = (
  state: EntityState<IntegrationConfig, string>,
  provider: IntegrationProvider,
  functionId: string,
): boolean => {
  const functions = selectAvailableFunctions(state, provider);
  // Function is enabled if it's in the list
  return functions.includes(functionId);
};
